import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import RightArrow from '@/images/arrows/RightArrow.svg';
import { useValidateUserCapping } from '@/pages/avatar';
import { useCreateAvatarStore } from '@/pages/avatar/common/context';
import { AvatarCreateTab, AvatarType, RequestStatus } from '@/pages/avatar/common/types';
import { useValidateUserPremiumPlusAvatarCapping } from '@/pages/avatar/hooks';
import premiumPlusCreateAvatarStore from '@/pages/avatar/PremiumPlusCreatePage/CreateAvatarStore';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import { creditsToVideoMinutes, useCreditQuery } from '@/state/credits/credits';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { PREMIUM_AVATAR_CREATION_COST_MINUTES } from '../../../PremiumPlusCreatePage/constants';

const StyledButton = styled(Button)`
    width: 16rem;
    font-size: 1.6rem;
    margin: 1px 15px 0 0;
    padding: 5px 16px 4px;
    display: flex;
    gap: 6px;
    align-items: center;
`;

const BackButton = styled(StyledButton)`
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-wrap: nowrap;
`;

export const HeaderButtons = observer(() => {
    const mixpanel = useAnalytics();
    const createAvatarStore = useCreateAvatarStore();
    const isDirectExpressFlowEnabled = useFlag('direct_express_flow_enabled', false);

    const { data: canCreatePremiumAvatar } = useValidateUserCapping();
    const { data: canCreatePremiumPlusAvatar } = useValidateUserPremiumPlusAvatarCapping();

    const firstTab = 0;
    const lastTab = createAvatarStore.tabs.length - 1;
    const beforeLastTab = lastTab - 1;

    const handleBack = () => {
        if (createAvatarStore.tab === firstTab) {
            return;
        }

        mixpanel?.track('avatar-create', {
            type: AvatarType.Express,
            event: 'click',
            action: 'back',
            page: AvatarCreateTab[createAvatarStore.tab],
        });
        createAvatarStore.setTab(createAvatarStore.tab - 1);
    };
    const { data: credits, isFetched: isCreditsFetched } = useCreditQuery();

    const hasEnoughCreditsForPremiumPlusAvatar =
        isCreditsFetched && creditsToVideoMinutes(credits!.remaining) >= PREMIUM_AVATAR_CREATION_COST_MINUTES;

    const handleNext = (action: 'next' | 'submit') => () => {
        if (createAvatarStore.tab === AvatarCreateTab.WAIT_FOR_UPLOADS) {
            return;
        }

        const isPremiumPlus = createAvatarStore.type === AvatarType.PremiumPlus;
        const isTabForUpgradePopup =
            createAvatarStore.tab === AvatarCreateTab.CHOOSE_AVATAR ||
            (isDirectExpressFlowEnabled && createAvatarStore.tab === AvatarCreateTab.INSTRUCTIONS);
        if (
            isTabForUpgradePopup &&
            (!canCreatePremiumAvatar ||
                (isPremiumPlus && !hasEnoughCreditsForPremiumPlusAvatar) ||
                (isPremiumPlus && canCreatePremiumPlusAvatar === false))
        ) {
            createAvatarStore.setUpgradeMinutesPopupOpen(true);
            return;
        }

        mixpanel?.track('avatar-create', {
            type: AvatarType.Express,
            event: 'click',
            action,
            page: AvatarCreateTab[createAvatarStore.tab],
        });

        createAvatarStore.setTab(createAvatarStore.tab + 1);
    };

    const hasPreviousNotHiddenTab = createAvatarStore.tabs.some((tab, index) => index < createAvatarStore.tab && !tab.hidden);
    const nextDisabled =
        (createAvatarStore.tab === AvatarCreateTab.FOOTAGE && !createAvatarStore.isFootageConfirmed) ||
        (createAvatarStore.type === AvatarType.PremiumPlus && premiumPlusCreateAvatarStore.idleVideoUploadStatus === RequestStatus.LOADING);
    const createDisabled = createAvatarStore.tab === AvatarCreateTab.CONSENT && !createAvatarStore.isValid;

    const isDraftAndAfterChooseAvatar = createAvatarStore.id && createAvatarStore.tab === AvatarCreateTab.INSTRUCTIONS;

    return (
        <ButtonsContainer>
            {![AvatarCreateTab.WAIT_FOR_UPLOADS].includes(createAvatarStore.tab) &&
                hasPreviousNotHiddenTab &&
                !isDraftAndAfterChooseAvatar && (
                    <BackButton variant="outlined" color="secondary" onClick={handleBack} aria-label="Back">
                        Back
                    </BackButton>
                )}

            {![beforeLastTab, AvatarCreateTab.WAIT_FOR_UPLOADS].includes(createAvatarStore.tab) && (
                <StyledButton
                    variant="contained"
                    color="secondary"
                    onClick={handleNext('next')}
                    aria-label="Next"
                    data-testid="avatars-page-next-button"
                    disabled={nextDisabled}>
                    Next
                    <MaskedIcon src={RightArrow} color="white" size="26px" />
                </StyledButton>
            )}

            {createAvatarStore.tab === beforeLastTab && (
                <CustomTooltip title="Please set correct username" disableHoverListener={createAvatarStore.usernameIsValid}>
                    <StyledButton
                        variant="contained"
                        color="secondary"
                        onClick={handleNext('submit')}
                        aria-label="Create Avatar"
                        data-testid="avatars-page-create-button"
                        disabled={createDisabled}>
                        Create Avatar
                    </StyledButton>
                </CustomTooltip>
            )}
        </ButtonsContainer>
    );
});
